import { Theme as MUITheme } from '@mint-lib/theme';

import type {} from '@mint-lib/theme/src/extensions/palette.js';
import type {} from '@mint-lib/theme/src/extensions/typography.js';

export const virtualTheme: Pick<MUITheme, 'palette' | 'typography'> = {
  palette: {
    common: {
      black: 'var(--common-black)',
      white: 'var(--common-white)',
    },
    mode: 'light',
    contrastThreshold: 0,
    tonalOffset: 0,
    primary: {
      light: 'var(--primary-light)',
      main: 'var(--primary-main)',
      dark: 'var(--primary-dark)',
      contrastText: 'var(--primary-contrastText)',
    },
    secondary: {
      light: 'var(--secondary-light)',
      main: 'var(--secondary-main)',
      dark: 'var(--secondary-dark)',
      contrastText: 'var(--secondary-contrastText)',
    },
    error: {
      main: 'var(--error-main)',
      light: 'var(--error-light)',
      dark: 'var(--error-dark)',
      hover: 'var(--error-hover)',
      active: 'var(--error-active)',
      contrastText: 'var(--error-contrastText)',
    },
    warning: {
      main: 'var(--warning-main)',
      light: 'var(--warning-light)',
      dark: 'var(--warning-dark)',
      hover: 'var(--warning-hover)',
      active: 'var(--warning-active)',
      contrastText: 'var(--warning-contrastText)',
    },
    info: {
      light: 'var(--info-light)',
      main: 'var(--info-main)',
      dark: 'var(--info-dark)',
      contrastText: 'var(--info-contrastText)',
    },
    success: {
      main: 'var(--success-main)',
      light: 'var(--success-light)',
      dark: 'var(--success-dark)',
      contrastText: 'var(--success-contrastText)',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
      A100: '#f5f5f5',
      A200: '#eeeeee',
      A400: '#bdbdbd',
      A700: '#616161',
    },
    text: {
      primary: 'var(--text-primary)',
      secondary: 'var(--text-secondary)',
      disabled: 'var(--text-disabled)',
      placeholder: 'var(--text-placeholder)',
      inverse: 'var(--text-inverse)',
      tertiary: 'var(--text-tertiary)',
      success: 'var(--text-success)',
      warning: 'var(--text-warning)',
      info: 'var(--text-info)',
      error: 'var(--text-error)',
      link: 'var(--text-link)',
      hoverPrimary: 'var(--text-hoverPrimary)',
      disabled02: 'var(--text-disabled02)',
    },
    divider: '',
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.04)',
      hoverOpacity: 0.04,
      selected: 'rgba(0, 0, 0, 0.08)',
      selectedOpacity: 0.08,
      disabled: 'rgba(0, 0, 0, 0.26)',
      disabledOpacity: 0.38,
      disabledBackground: 'var(--action-disabledBackground)',
      focus: 'rgba(0, 0, 0, 0.12)',
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
      disabled01: 'var(--action-disabled01)',
      disabled02: 'var(--action-disabled02)',
      focusBorder: 'var(--action-focusBorder)',
      inverseFocusBorder: 'var(--action-inverseFocusBorder)',
    },
    background: {
      default: 'var(--background-default)',
      primary: 'var(--background-primary)',
      secondary: 'var(--background-secondary)',
      tertiary: 'var(--background-tertiary)',
      error: 'var(--background-error)',
      success: 'var(--background-success)',
      warning: 'var(--background-warning)',
      info: 'var(--background-info)',
      purple: 'var(--background-purple)',
      pink: 'var(--background-pink)',
      rose: 'var(--background-rose)',
      blue: 'var(--background-blue)',
      yellow: 'var(--background-yellow)',
      green: 'var(--background-green)',
      peach: 'var(--background-peach)',
      azure: 'var(--background-azure)',
      violet: 'var(--background-violet)',
      lime: 'var(--background-lime)',
      orange: 'var(--background-orange)',
      ice: 'var(--background-ice)',
      fuchsia: 'var(--background-fuchsia)',
      emerald: 'var(--background-emerald)',
      teal: 'var(--background-teal)',
      mint: 'var(--background-mint)',
      royal: 'var(--background-royal)',
      blaze: 'var(--background-blaze)',

      paper: '#ffffff',
    },
    getContrastText: function (): string {
      throw new Error('Function not implemented.');
    },
    augmentColor: function () {
      throw new Error('Function not implemented.');
    },
    icon: {
      primary: 'var(--icon-primary)',
      secondary: 'var(--icon-secondary)',
      tertiary: 'var(--icon-tertiary)',
      inverse: 'var(--icon-inverse)',
    },
    ui: {
      primary: 'var(--ui-primary)',
      secondary: 'var(--ui-secondary)',
      tertiary: 'var(--ui-tertiary)',
      inverse01: 'var(--ui-inverse01)',
      inverse02: 'var(--ui-inverse02)',
      field01: 'var(--ui-field01)',
      field02: 'var(--ui-field02)',
      overlay: 'var(--ui-overlay)',
      hover: 'var(--ui-hover)',
      selected: 'var(--ui-selected)',
      active: 'var(--ui-active)',
      hoverSelected: 'var(--ui-hoverSelected)',
      hoverRow: 'var(--ui-hoverRow)',
      hoverCell: 'var(--ui-hoverCell)',
      highlight: 'var(--ui-highlight)',
    },
    tertiary: {
      main: 'var(--tertiary-main)',
      light: 'var(--tertiary-light)',
      dark: 'var(--tertiary-dark)',
      active: 'var(--tertiary-active)',
      hover: 'var(--tertiary-hover)',
      contrastText: 'var(--tertiary-contrastText)',
    },
    greyscale: {
      black: 'var(--greyscale-black)',
      grey80: 'var(--greyscale-grey80)',
      grey70: 'var(--greyscale-grey70)',
      grey60: 'var(--greyscale-grey60)',
      grey50: 'var(--greyscale-grey50)',
      grey40: 'var(--greyscale-grey40)',
      grey30: 'var(--greyscale-grey30)',
      grey20: 'var(--greyscale-grey20)',
      grey10: 'var(--greyscale-grey10)',
      white: 'var(--greyscale-white)',
    },
    transparent: {
      main: 'var(--transparent-main)',
      contrastText: 'var(--transparent-contrastText)',
    },
    yellow: {
      main: 'var(--yellow-main)',
      contrastText: 'var(--yellow-contrastText)',
      dark: 'var(--yellow-dark)',
    },
    purple: {
      main: 'var(--purple-main)',
      contrastText: 'var(--purple-contrastText)',
      dark: 'var(--purple-dark)',
    },
    teal: {
      main: 'var(--teal-main)',
      contrastText: 'var(--teal-contrastText)',
    },
    dark: {
      main: 'var(--dark-main)',
      contrastText: 'var(--dark-contrastText)',
    },
    silver: {
      main: 'var(--silver-main)',
      contrastText: 'var(--silver-contrastText)',
    },
    azure: {
      main: 'var(--azure-main)',
      contrastText: 'var(--azure-contrastText)',
    },
    peach: {
      main: 'var(--peach-main)',
      contrastText: 'var(--peach-contrastText)',
    },
    violet: {
      main: 'var(--violet-main)',
      contrastText: 'var(--violet-contrastText)',
      dark: 'var(--violet-dark)',
    },
    green: {
      main: 'var(--green-main)',
      contrastText: 'var(--green-contrastText)',
      dark: 'var(--green-dark)',
    },
    ice: {
      main: 'var(--ice-main)',
      contrastText: 'var(--ice-contrastText)',
      dark: 'var(--ice-dark)',
    },
    orange: {
      main: 'var(--orange-main)',
      contrastText: 'var(--orange-contrastText)',
      dark: 'var(--orange-dark)',
    },
    emerald: {
      main: 'var(--emerald-main)',
      contrastText: 'var(--emerald-contrastText)',
      dark: 'var(--emerald-dark)',
    },
    lime: {
      main: 'var(--lime-main)',
      contrastText: 'var(--lime-contrastText)',
      dark: 'var(--lime-dark)',
    },
    mint: {
      main: 'var(--mint-main)',
      contrastText: 'var(--mint-contrastText)',
      dark: 'var(--mint-dark)',
    },
    blue: {
      main: 'var(--blue-main)',
      contrastText: 'var(--blue-contrastText)',
      dark: 'var(--blue-dark)',
    },
    fuchsia: {
      main: 'var(--fuchsia-main)',
      contrastText: 'var(--fuchsia-contrastText)',
      dark: 'var(--fuchsia-dark)',
    },
    royal: {
      main: 'var(--royal-main)',
      contrastText: 'var(--royal-contrastText)',
      dark: 'var(--royal-dark)',
    },

    blaze: {
      main: 'var(--blaze-main)',
      contrastText: 'var(--blaze-contrastText)',
      dark: 'var(--blaze-dark)',
    },
    dataVisualization: {
      blue200: 'var(--dataVisualization-blue200)',
      blue400: 'var(--dataVisualization-blue400)',
      blue600: 'var(--dataVisualization-blue600)',
      blue800: 'var(--dataVisualization-blue800)',
      fuchsia200: 'var(--dataVisualization-fuchsia200)',
      fuchsia400: 'var(--dataVisualization-fuchsia400)',
      fuchsia600: 'var(--dataVisualization-fuchsia600)',
      fuchsia800: 'var(--dataVisualization-fuchsia800)',
      orange200: 'var(--dataVisualization-orange200)',
      orange400: 'var(--dataVisualization-orange400)',
      orange600: 'var(--dataVisualization-orange600)',
      orange800: 'var(--dataVisualization-orange800)',
      emerald200: 'var(--dataVisualization-emerald200)',
      emerald400: 'var(--dataVisualization-emerald400)',
      emerald600: 'var(--dataVisualization-emerald600)',
      emerald800: 'var(--dataVisualization-emerald800)',
      yellow200: 'var(--dataVisualization-yellow200)',
      yellow400: 'var(--dataVisualization-yellow400)',
      yellow600: 'var(--dataVisualization-yellow600)',
      yellow800: 'var(--dataVisualization-yellow800)',
      violet200: 'var(--dataVisualization-violet200)',
      violet400: 'var(--dataVisualization-violet400)',
      violet600: 'var(--dataVisualization-violet600)',
      violet800: 'var(--dataVisualization-violet800)',
      red200: 'var(--dataVisualization-red200)',
      red400: 'var(--dataVisualization-red400)',
      red600: 'var(--dataVisualization-red600)',
      red800: 'var(--dataVisualization-red800)',
      lime200: 'var(--dataVisualization-lime200)',
      lime400: 'var(--dataVisualization-lime400)',
      lime600: 'var(--dataVisualization-lime600)',
      lime800: 'var(--dataVisualization-lime800)',
    },
    border: {
      primary: 'var(--border-primary)',
      secondary: 'var(--border-secondary)',
      disabled: 'var(--border-disabled)',
      tertiary: 'var(--border-tertiary)',
      focus: 'var(--border-focus)',
      focusPrimary: 'var(--border-focusPrimary)',
      focusError: 'var(--border-focusError)',
      focusWarning: 'var(--border-focusWarning)',
      selectedPrimary: 'var(--border-selectedPrimary)',
      selectedSecondary: 'var(--border-selectedSecondary)',
    },
    channel: {
      metasearch: 'var(--channel-metasearch)',
      metasearchBackground: 'var(--channel-metasearchBackground)',
      payTV: 'var(--channel-payTV)',
      payTVBackground: 'var(--channel-payTVBackground)',
      mgm: 'var(--channel-mgm)',
      mgmBackground: 'var(--channel-mgmBackground)',
      display: 'var(--channel-display)',
      displayBackground: 'var(--channel-displayBackground)',
      adserver: 'var(--channel-adserver)',
      adserverBackground: 'var(--channel-adserverBackground)',
      affiliation: 'var(--channel-affiliation)',
      affiliationBackground: 'var(--channel-affiliationBackground)',
      direct: 'var(--channel-direct)',
      directBackground: 'var(--channel-directBackground)',
      organic: 'var(--channel-organic)',
      organicBackground: 'var(--channel-organicBackground)',
      ooh: 'var(--channel-ooh)',
      oohBackground: 'var(--channel-oohBackground)',
      webAnalytics: 'var(--channel-webAnalytics)',
      webAnalyticsBackground: 'var(--channel-webAnalyticsBackground)',
      brandedContent: 'var(--channel-brandedContent)',
      brandedContentBackground: 'var(--channel-brandedContentBackground)',
      influencerMarketing: 'var(--channel-influencerMarketing)',
      influencerMarketingBackground:
        'var(--channel-influencerMarketingBackground)',
      dsp: 'var(--channel-dsp)',
      dspBackground: 'var(--channel-dspBackground)',
      chatBot: 'var(--channel-chatBot)',
      chatBotBackground: 'var(--channel-chatBotBackground)',
      brandSafety: 'var(--channel-brandSafety)',
      brandSafetyBackground: 'var(--channel-brandSafetyBackground)',
      programmaticRadio: 'var(--channel-programmaticRadio)',
      programmaticRadioBackground: 'var(--channel-programmaticRadioBackground)',
      network: 'var(--channel-network)',
      networkBackground: 'var(--channel-networkBackground)',
      offlineRadio: 'var(--channel-offlineRadio)',
      offlineRadioBackground: 'var(--channel-offlineRadioBackground)',
      publisher: 'var(--channel-publisher)',
      publisherBackground: 'var(--channel-publisherBackground)',
      mobile: 'var(--channel-mobile)',
      mobileBackground: 'var(--channel-mobileBackground)',
      dmp: 'var(--channel-dmp)',
      dmpBackground: 'var(--channel-dmpBackground)',
      dooh: 'var(--channel-dooh)',
      doohBackground: 'var(--channel-doohBackground)',
      dem: 'var(--channel-dem)',
      demBackground: 'var(--channel-demBackground)',
      offlineTV: 'var(--channel-offlineTV)',
      offlineTVBackground: 'var(--channel-offlineTVBackground)',
      video: 'var(--channel-video)',
      videoBackground: 'var(--channel-videoBackground)',
      addressableTV: 'var(--channel-addressableTV)',
      addressableTVBackground: 'var(--channel-addressableTVBackground)',
      social: 'var(--channel-social)',
      socialBackground: 'var(--channel-socialBackground)',
      native: 'var(--channel-native)',
      nativeBackground: 'var(--channel-nativeBackground)',
      search: 'var(--channel-search)',
      searchBackground: 'var(--channel-searchBackground)',
      promocode: 'var(--channel-promocode)',
      promocodeBackground: 'var(--channel-promocodeBackground)',
      events: 'var(--channel-events)',
      eventsBackground: 'var(--channel-eventsBackground)',
      newspaper: 'var(--channel-newspaper)',
      newspaperBackground: 'var(--channel-newspaperBackground)',
      cinema: 'var(--channel-cinema)',
      cinemaBackground: 'var(--channel-cinemaBackground)',
      magazine: 'var(--channel-magazine)',
      magazineBackground: 'var(--channel-magazineBackground)',
      directMedia: 'var(--channel-directMedia)',
      directMediaBackground: 'var(--channel-directMediaBackground)',
      paidProgrammatic: 'var(--channel-paidProgrammatic)',
      paidProgrammaticBackground: 'var(--channel-paidProgrammaticBackground)',
      retail: 'var(--channel-retail)',
      retailBackground: 'var(--channel-retailBackground)',
    },
    ai: {
      lightCyan: 'var(--ai-lightCyan)',
      lightGreen: 'var(--ai-lightGreen)',
      gradient: 'linear-gradient(to left bottom, #82F570, #79ECEC)',
      hover: 'linear-gradient( #6BD754, #54D5D5)',
      selected: 'linear-gradient( #50B441, #44B2B2)',
      active: 'linear-gradient( #50B441, #44B2B2)',
      focus: 'linear-gradient( #C6FFBE, #BDFFFF)',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2rem',
      lineHeight: '2.5rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.5625rem',
      lineHeight: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.3125rem',
      lineHeight: '1.5rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.0625rem',
      lineHeight: '1.25rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '0.9375rem',
      lineHeight: '1.25rem',
      fontWeight: 500,
      textTransform: 'uppercase',
    },
    h6: {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 600,
    },
    bodyShort01: {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 400,
    },
    bodyShort02: {
      fontSize: '0.875rem',
      lineHeight: '1rem',
      fontWeight: 500,
    },
    bodyLong01: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
    },
    bodyLong02: {
      fontSize: '0.875rem',
      lineHeight: '1.25rem',
      fontWeight: 500,
    },
    bodyMobile01: {
      fontSize: '1.125rem',
      lineHeight: '1.25rem',
      fontWeight: 400,
    },
    bodyMobile02: {
      fontSize: '1.125rem',
      lineHeight: '1.375rem',
      fontWeight: 500,
    },
    bodyMobile03: {
      fontSize: '1.125rem',
      lineHeight: '1.5rem',
      fontWeight: 590,
    },
    caption01: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      fontWeight: 500,
    },
    caption02: {
      fontSize: '0.6875rem',
      lineHeight: '0.75rem',
    },
    caption03: {
      fontSize: '0.5rem',
      lineHeight: '0.75rem',
      fontWeight: 400,
    },
    overline01: {
      fontSize: '0.6875rem',
      lineHeight: '0.75rem',
      textTransform: 'uppercase',
      fontWeight: 400,
    },
    label01: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    helperText01: {
      fontSize: '0.75rem',
      lineHeight: '1rem',
    },
    subtitle1: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.75,
      letterSpacing: '0.00938em',
    },
    subtitle2: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.57,
      letterSpacing: '0.00714em',
    },
    body1: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
      letterSpacing: '0.00938em',
    },
    body2: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      letterSpacing: '0.01071em',
    },
    caption: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    button: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 500,
      fontSize: '0.875rem',
      lineHeight: 1.75,
      letterSpacing: '0.02857em',
      textTransform: 'uppercase',
    },
    overline: {
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif"`,
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 2.66,
      letterSpacing: '0.08333em',
      textTransform: 'uppercase',
    },
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    htmlFontSize: 16,
    pxToRem: function (px: number): string {
      return px / 16 + 'rem';
    },
  },
};
